/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * useTouchDevice - Detects touch-capable devices
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useState, useEffect } from 'react'

/**
 * Hook to detect if the current device is touch-capable
 * Uses CSS media query (pointer: coarse) as primary detection
 *
 * @returns {boolean} True if device is touch-capable
 */
export const useTouchDevice = () => {
  const [isTouchDevice, setIsTouchDevice] = useState(false)

  useEffect(() => {
    const checkTouchDevice = () => {
      // Primary check: CSS media query for coarse pointer (touch screens)
      const hasTouchPointer = window.matchMedia('(pointer: coarse)').matches

      // Fallback checks
      const hasTouchEvents = 'ontouchstart' in window
      const hasMaxTouchPoints = navigator.maxTouchPoints > 0

      return hasTouchPointer || (hasTouchEvents && hasMaxTouchPoints)
    }

    setIsTouchDevice(checkTouchDevice())

    // Listen for changes (e.g., connecting/disconnecting touch devices)
    const mediaQuery = window.matchMedia('(pointer: coarse)')
    const handleChange = (e) => setIsTouchDevice(e.matches)

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  return isTouchDevice
}

export default useTouchDevice
